/**
 * The four situation questions, as answer chips. Optional, skippable: an
 * unanswered question is simply absent from the vector, and clicking the
 * selected answer clears it.
 *
 * Used at event setup (EngagementSetup) and on the session Setup tab
 * (SituationPanel). Describes the process, never the people.
 */

import {
  SITUATION_QUESTIONS,
  isEmptySituationVector,
  type SituationDimension,
  type SituationVector,
} from '../../../types/prioritization/situationVector';

interface Props {
  value: SituationVector | null | undefined;
  onChange: (next: SituationVector | null) => void;
  disabled?: boolean;
  /** Two columns on wide screens (default) or a single column. */
  columns?: 1 | 2;
}

export function SituationQuestions({ value, onChange, disabled, columns = 2 }: Props) {
  const vector: SituationVector = value ?? {};

  const toggle = (dimension: SituationDimension, optionValue: string) => {
    const next: Record<string, string> = { ...(vector as Record<string, string>) };
    if (next[dimension] === optionValue) delete next[dimension];
    else next[dimension] = optionValue;
    const typed = next as SituationVector;
    onChange(isEmptySituationVector(typed) ? null : typed);
  };

  return (
    <div className={columns === 2 ? 'grid gap-4 sm:grid-cols-2' : 'grid gap-4'}>
      {SITUATION_QUESTIONS.map((q) => {
        const current = vector[q.dimension];
        return (
          <fieldset key={q.dimension} className="min-w-0 space-y-1.5" data-testid={`situation-${q.dimension}`}>
            <legend className="text-[11px] font-semibold uppercase tracking-wide text-[var(--text-ter)]">
              {q.title}
            </legend>
            <p className="text-sm leading-snug text-[var(--text-pri)]">{q.question}</p>
            <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label={q.title}>
              {q.options.map((o) => {
                const selected = current === o.value;
                return (
                  <button
                    key={o.value}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    disabled={disabled}
                    onClick={() => toggle(q.dimension, o.value)}
                    className={[
                      'border px-2.5 py-1 text-xs font-medium transition disabled:opacity-60',
                      selected
                        ? 'border-slate-blue-500 bg-slate-blue-50 text-slate-blue-800 ring-1 ring-slate-blue-200 dark:bg-slate-blue-900/30 dark:text-slate-blue-200 dark:ring-slate-blue-700'
                        : 'border-[var(--rule)] bg-[var(--bg-raised)] text-[var(--text-sec)] hover:border-slate-blue-400',
                    ].join(' ')}
                  >
                    {o.label}
                  </button>
                );
              })}
            </div>
          </fieldset>
        );
      })}
    </div>
  );
}
